import type { CCPreset } from '~/types/presets';
import type { MidiCC } from '~/types/midi';
import { getYoTestSynthDefaultProfile } from '~/lib/profiles/yoTestSynthProfile';

/**
 * "Well Rounded" CC preset for YO! Monosynth
 *
 * This is the default preset that loads after a reset. It provides a carefully
 * tuned set of MIDI CC parameters optimized for subtractive synthesis with
 * balanced envelope settings and moderate filter characteristics.
 *
 * Key characteristics:
 * - Fast filter envelope attack (CC16: 95) for punchy transients
 * - Very short filter decay (CC17: 1) for percussive filter movement
 * - High filter sustain (CC18: 113) maintains brightness
 * - Moderate filter release (CC19: 61) for smooth note endings
 * - Low filter base frequency (CC20: 5) for warm, filtered sound
 * - High filter octaves (CC21: 75) for expressive filter range
 * - High filter cutoff (CC70: 99) for bright, open sound
 * - Moderate-high resonance (CC71: 67) for character
 * - Long amp release (CC72: 115) for sustained notes
 * - Moderate amp sustain (CC73: 61) for balanced dynamics
 * - High amp decay (CC74: 104) for full-bodied sound
 * - Moderate-long filter decay (CC75: 58) for evolving timbre
 * - Low oscillator parameter (CC76: 14) for subtle movement
 *
 * This preset creates a warm, expressive sound suitable for bass lines,
 * leads, and pads with good articulation and filter movement.
 */

const wellRoundedMonosynthCCs: MidiCC[] = [
  { number: 1, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 7, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 11, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 16, value: 95, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 17, value: 1, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 18, value: 113, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 19, value: 61, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 20, value: 5, scramble: true, smooth: false, show: true, min: 0, max: 70 },
  { number: 21, value: 75, scramble: true, smooth: false, show: true, min: 20, max: 100 },
  { number: 70, value: 99, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 71, value: 67, scramble: true, smooth: false, show: true, min: 0, max: 80 },
  { number: 72, value: 115, scramble: true, smooth: false, show: true, min: 10, max: 127 },
  { number: 73, value: 61, scramble: true, smooth: false, show: true, min: 3, max: 80 },
  { number: 74, value: 104, scramble: true, smooth: false, show: true, min: 50, max: 110 },
  { number: 75, value: 58, scramble: true, smooth: false, show: true, min: 10, max: 127 },
  { number: 76, value: 14, scramble: true, smooth: false, show: true, min: 0, max: 127 },
];

/**
 * "Well Rounded" Monosynth CC Preset
 */
export const WELL_ROUNDED_MONOSYNTH_PRESET: CCPreset = {
  id: 'well-rounded-mono',
  name: 'Well Rounded',
  deviceType: 'MonoSynth',
  cc: wellRoundedMonosynthCCs,
  profiles: [getYoTestSynthDefaultProfile()],
  activeProfileId: 'yo-test-synth-default',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the "Well Rounded" Monosynth CC preset
 * Returns a deep copy to avoid mutation
 */
export function getWellRoundedMonosynthPreset(): CCPreset {
  return {
    ...WELL_ROUNDED_MONOSYNTH_PRESET,
    cc: WELL_ROUNDED_MONOSYNTH_PRESET.cc.map(cc => ({ ...cc })),
    profiles: WELL_ROUNDED_MONOSYNTH_PRESET.profiles.map(profile => ({
      ...profile,
      ccConfig: profile.ccConfig.map(config => ({ ...config })),
    })),
  };
}
